import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../packages/auth-client';
import { rootClient, getErrorMessage } from '../packages/api-client';
import { Button, Spinner } from '../packages/ui';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../hooks/useTheme';

/**
 * OAuthConsentPage — the authorization_endpoint of the MCP OAuth flow.
 *
 * claude.ai redirects the user's browser here with the OAuth params. If the user
 * isn't signed in we bounce through the normal login (returning here after), then
 * show an explicit consent screen. On approval we ask the backend to mint an auth
 * code and redirect back to the client's redirect_uri.
 */
export function OAuthConsentPage() {
    usePageTitle('Authorize connection');
    const { theme } = useTheme();
    const { isAuthenticated, isLoading, user } = useAuth();
    const [params] = useSearchParams();
    const [clientName, setClientName] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);

    const clientId = params.get('client_id') ?? '';
    const redirectUri = params.get('redirect_uri') ?? '';
    const state = params.get('state') ?? '';
    const codeChallenge = params.get('code_challenge') ?? '';
    const codeChallengeMethod = params.get('code_challenge_method') ?? '';
    const scope = params.get('scope') ?? '';

    // Not signed in → store this URL and route through login; we return here after.
    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) {
            sessionStorage.setItem('postLoginRedirect', window.location.pathname + window.location.search);
            window.location.assign('/login');
        }
    }, [isLoading, isAuthenticated]);

    // Resolve the client name for the consent copy.
    useEffect(() => {
        if (!isAuthenticated || !clientId) return;
        rootClient.get<{ client_name: string }>(`/oauth/clients/${encodeURIComponent(clientId)}`)
            .then(res => setClientName(res.data.client_name || 'A connector'))
            .catch(() => setClientName('A connector'));
    }, [isAuthenticated, clientId]);

    const invalid = !clientId || !redirectUri || params.get('response_type') !== 'code'
        || !codeChallenge || codeChallengeMethod !== 'S256';

    const decide = async (approve: boolean) => {
        setSubmitting(true);
        setError('');
        try {
            const { data } = await rootClient.post<{ redirect_to: string }>('/oauth/authorize/decision', {
                client_id: clientId, redirect_uri: redirectUri, code_challenge: codeChallenge,
                code_challenge_method: codeChallengeMethod, state, scope, approve,
            });
            window.location.assign(data.redirect_to);
        } catch (err) {
            setError(getErrorMessage(err));
            setSubmitting(false);
        }
    };

    if (isLoading || !isAuthenticated) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spinner size="lg" /></div>;
    }

    return (
        <div className="login-container">
            <div className="login-card" style={{ maxWidth: 440 }}>
                <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>
                <h1 className="login-title">Authorize connection</h1>

                {invalid ? (
                    <p className="login-subtitle" style={{ color: 'var(--danger)' }}>
                        This authorization request is missing or has invalid parameters. Please start the connection again from the app you’re linking.
                    </p>
                ) : (
                    <>
                        <p className="login-subtitle">
                            <strong>{clientName || 'A connector'}</strong> wants to connect to your KaziAtlas workspace and act on your behalf
                            {user?.email ? <> as <strong>{user.email}</strong></> : null}.
                        </p>
                        <p style={{ fontSize: '0.85rem', color: 'var(--ink-muted)', margin: '0 0 1.25rem' }}>
                            It will be able to read and manage your projects and tasks — limited to what your account can already access.
                        </p>

                        {error && <p style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{error}</p>}

                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <Button variant="ghost" className="w-full" onClick={() => decide(false)} disabled={submitting}>
                                Deny
                            </Button>
                            <Button variant="primary" className="w-full" onClick={() => decide(true)} isLoading={submitting}>
                                Allow
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
